import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MessageCircle, Phone, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

// Service images
const serviceImages = {
  nannies: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/b3ydcnwi_6C3A63A4-ADD4-48EB-A742-49B6F86E37A6.png',
  housemaids: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/eqsxnt3c_CE884309-4EEE-4977-9FFF-12D57B43FB5D.png',
  security: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/2p9w37dl_2D71998B-BCB4-4089-AA62-7577120313D6.png',
  cooks: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/s3j6hk8u_698500F0-BF3D-4EB4-A056-DBD7490F3B99.png',
  drivers: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/mlmxg0k3_B047AACB-3EC8-4943-B06B-05118D04FAC7.png',
  teachers: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/50rj7bhl_72820E11-F035-49EA-BC39-78D250D08F5E.png',
  caregivers: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/dcsulqtz_3A6BE1B6-FA4B-49C5-A392-8863E62A65F2.png',
  farmers: 'https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/61i5wi50_362B1575-47C3-415B-9358-2F7E2A86F9BD.png',
};

// Premium Payment Section Component with Cinematic Motion
const PaymentSection = () => {
  const { t, isRTL } = useLanguage();
  const sectionRef = useRef(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleMediaChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (sectionRef.current) {
            const rect = sectionRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            if (rect.top < windowHeight && rect.bottom > 0) {
              const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
              const offset = (scrollProgress - 0.5) * 16;
              setParallaxOffset(Math.max(-8, Math.min(8, offset)));
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prefersReducedMotion]);

  return (
    <div ref={sectionRef} className="mt-16 pt-12 border-t border-border/30">
      <div 
        className={prefersReducedMotion ? '' : 'payment-parallax-wrapper'}
        style={prefersReducedMotion ? {} : { transform: `translate3d(0, ${parallaxOffset}px, 0)` }}
      >
        <div className={`flex flex-col items-center justify-center gap-5 ${prefersReducedMotion ? '' : 'payment-section-premium'}`}>
          <p className={`text-muted-foreground text-sm md:text-base text-center font-medium tracking-wide ${prefersReducedMotion ? '' : 'payment-text-breath'}`}>
            {t('services.payInInstallments')}
          </p>
          <div className={`flex items-center justify-center gap-8 md:gap-12 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`${prefersReducedMotion ? '' : 'payment-logo-tabby'} opacity-90 hover:opacity-100 transition-opacity duration-500`}>
              <img
                src="https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/tmrvhizd_IMG_8199.png"
                alt="Tabby"
                className="h-11 md:h-14 w-auto object-contain rounded-lg"
                loading="lazy"
              />
            </div>
            <div className={`${prefersReducedMotion ? '' : 'payment-logo-tamara'} opacity-90 hover:opacity-100 transition-opacity duration-500`}>
              <img
                src="https://customer-assets.emergentagent.com/job_my-digital-home-7/artifacts/3vn807dv_IMG_8200.png"
                alt="Tamara"
                className="h-11 md:h-14 w-auto object-contain rounded-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const { t, isRTL } = useLanguage();

  // Get translated services data
  const servicesData = [
    { id: 'nannies', image: serviceImages.nannies, color: 'uae-red' },
    { id: 'housemaids', image: serviceImages.housemaids, color: 'uae-green' },
    { id: 'security', image: serviceImages.security, color: 'uae-red' },
    { id: 'cooks', image: serviceImages.cooks, color: 'uae-green' },
    { id: 'drivers', image: serviceImages.drivers, color: 'uae-red' },
    { id: 'teachers', image: serviceImages.teachers, color: 'uae-green' },
    { id: 'caregivers', image: serviceImages.caregivers, color: 'uae-red' },
    { id: 'farmers', image: serviceImages.farmers, color: 'uae-green' },
  ];

  const handleEnquiry = (serviceName) => {
    const message = encodeURIComponent(
      `Hello! I'm interested in your ${serviceName} services. Please provide more information.`
    );
    window.open(`https://wa.me/97167796889?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Page Header */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container-custom text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            {t('services.pageTitle')} <span className="text-gradient-uae">{t('services.pageTitleHighlight')}</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('services.pageSubtitle')}
          </p>
          {/* UAE Flag Accent */}
          <div className="flex justify-center gap-2 mt-8">
            <div className="w-12 h-1.5 bg-uae-red rounded-full" />
            <div className="w-12 h-1.5 bg-uae-green rounded-full" />
            <div className="w-12 h-1.5 bg-foreground rounded-full" />
          </div>
        </div>
      </section>

      {/* Services List - Vertical Single Column */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="space-y-8">
            {servicesData.map((service, index) => {
              const serviceTranslation = t(`services.${service.id}`);
              
              return (
                <Card
                  key={service.id}
                  className="bg-card border-border overflow-hidden card-hover group"
                >
                  <div className={`grid md:grid-cols-5 gap-0 ${isRTL ? 'md:grid-flow-dense' : ''}`}>
                    {/* Image */}
                    <div className={`md:col-span-2 relative bg-muted min-h-[200px] md:min-h-full ${isRTL ? 'md:col-start-4' : ''}`}>
                      <img
                        src={service.image}
                        alt={serviceTranslation.title}
                        className="w-full h-full object-cover"
                      />
                      <div className={`absolute bottom-0 left-0 right-0 h-1 ${service.color === 'uae-red' ? 'bg-uae-red' : 'bg-uae-green'}`} />
                    </div>

                    {/* Content */}
                    <div className={`md:col-span-3 p-6 md:p-8 flex flex-col ${isRTL ? 'md:col-start-1 text-right' : ''}`}>
                      <CardHeader className="p-0 mb-4">
                        <CardTitle className={`font-display text-2xl text-foreground group-hover:text-uae-red transition-colors`}>
                          {serviceTranslation.title}
                        </CardTitle>
                        <CardDescription className="text-muted-foreground mt-2 leading-relaxed">
                          {serviceTranslation.description}
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="p-0 flex-grow">
                        {/* Features */}
                        <div className="grid grid-cols-2 gap-2 mb-6">
                          {serviceTranslation.features.map((feature) => (
                            <div key={feature} className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <CheckCircle className={`w-4 h-4 flex-shrink-0 ${service.color === 'uae-red' ? 'text-uae-red' : 'text-uae-green'}`} />
                              <span className="text-sm text-muted-foreground">{feature}</span>
                            </div>
                          ))}
                        </div>

                        {/* CTA */}
                        <div className={`flex flex-col sm:flex-row gap-3 mt-auto ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                          <Button
                            variant={service.color === 'uae-red' ? 'uaeRed' : 'uaeGreen'}
                            size="default"
                            className="gap-2"
                            onClick={() => handleEnquiry(serviceTranslation.title)}
                          >
                            <MessageCircle className="w-4 h-4" />
                            {t('services.enquireNow')}
                          </Button>
                          <Button
                            variant="outline"
                            size="default"
                            className="gap-2"
                            onClick={() => window.open('tel:+97167796889', '_self')}
                          >
                            <Phone className="w-4 h-4" />
                            {t('services.callUs')}
                          </Button>
                        </div>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Payment Section */}
          <PaymentSection />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-r from-uae-red to-uae-green">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-uae-white mb-4">
            {t('services.cantFind')}
          </h2>
          <p className="text-uae-white/80 mb-8 max-w-2xl mx-auto">
            {t('services.cantFindSubtitle')}
          </p>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <Button
              variant="default"
              size="xl"
              className="bg-uae-white text-uae-black hover:bg-uae-white/90 gap-2"
              onClick={() => window.open('https://wa.me/97167796889', '_blank')}
            >
              <MessageCircle className="w-5 h-5" />
              {t('services.chatOnWhatsApp')}
            </Button>
            <Button
              variant="outline"
              size="xl"
              className="border-uae-white text-uae-white hover:bg-uae-white/10 gap-2"
              onClick={() => window.open('tel:+97167796889', '_self')}
            >
              <Phone className="w-5 h-5" />
              +971 6 779 6889
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
